(() => {
  const ADA_HOST_SELECTOR = 'csm-web-accessibility';
  const CONSENTMO_HOST_SELECTOR = '.csm-cookie-consent';
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  const ADA_Z_INDEX = 2147483646;
  const BASE_GAP_MOBILE = 12;
  const BASE_GAP_DESKTOP = 16;
  const BASE_SIDE_GAP_MOBILE = 12;
  const BASE_SIDE_GAP_DESKTOP = 16;
  const KLAVIYO_TEASER_SELECTOR = "div.needsclick[class*='kl-teaser-']";
  const MIN_CONTROL_SIZE = 20;
  const MAX_CONTROL_SIZE = 140;
  const CONSENT_BANNER_SELECTORS = [
    '.isense-cookieconsent-wrapper',
    '.csm-cookie-box',
    '.csm-dialog',
    '.cc-window',
    '.isense-cc-window',
    '#shopify-pc__banner',
    '.shopify-pc__banner',
  ];
  const ADA_CONTROL_SELECTORS = [
    '#csm-ada-compliance-wrapper',
    '.csm-ada-compliance',
    '.csm-accessibility-link',
  ];
  const CONSENT_REOPEN_SELECTORS = [
    '.isense-reopen-widget-button',
    '.isense-reopen-widget-link',
    '.csm-cookie-widget-button',
    '#csm-cookie-widget-button',
    '.csm-cc-reopen-widget',
    '[data-csm-cookie-reopen]',
  ];

  let isQueued = false;

  function isSafariBrowser() {
    const userAgent = window.navigator.userAgent;
    return /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);
  }

  function getViewportHeight() {
    const visualViewport = window.visualViewport;
    if (visualViewport && Number.isFinite(visualViewport.height) && visualViewport.height > 0) {
      return visualViewport.height;
    }
    return window.innerHeight;
  }

  function isVisible(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number.parseFloat(style.opacity) === 0) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function getBottomOverlap(element) {
    if (!isVisible(element)) {
      return 0;
    }

    const style = window.getComputedStyle(element);
    if (!['fixed', 'sticky'].includes(style.position)) {
      return 0;
    }

    const viewportHeight = getViewportHeight();
    const rect = element.getBoundingClientRect();
    if (rect.bottom < viewportHeight - 1) {
      return 0;
    }

    return Math.max(0, viewportHeight - Math.max(0, rect.top));
  }

  function getFloatingPositionTarget(element) {
    let current = element;
    while (current instanceof HTMLElement) {
      const style = window.getComputedStyle(current);
      if (style.position === 'fixed' || style.position === 'sticky') {
        return current;
      }
      current = current.parentElement;
    }
    return element instanceof HTMLElement ? element : null;
  }

  function isFloatingControlCandidate(element, side) {
    if (!(element instanceof HTMLElement) || !isVisible(element)) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (!['fixed', 'sticky'].includes(style.position)) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (
      rect.width < MIN_CONTROL_SIZE ||
      rect.height < MIN_CONTROL_SIZE ||
      rect.width > MAX_CONTROL_SIZE ||
      rect.height > MAX_CONTROL_SIZE
    ) {
      return false;
    }

    const viewportHeight = getViewportHeight();
    if (rect.top < viewportHeight * 0.45) {
      return false;
    }

    if (side === 'left' && rect.left > window.innerWidth * 0.55) {
      return false;
    }

    if (side === 'right' && rect.right < window.innerWidth * 0.45) {
      return false;
    }

    return true;
  }

  function dedupeElements(elements) {
    const seen = new Set();
    return elements.filter((element) => {
      if (!(element instanceof HTMLElement) || seen.has(element)) {
        return false;
      }
      seen.add(element);
      return true;
    });
  }

  function collectElementsBySelectors(root, selectors) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return [];
    }

    const collected = [];
    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => {
        if (element instanceof HTMLElement) {
          collected.push(element);
        }
      });
    });
    return collected;
  }

  function collectShadowControls(shadowRoot, selectors, side) {
    if (!shadowRoot) {
      return [];
    }

    const controls = [];
    const explicitMatches = collectElementsBySelectors(shadowRoot, selectors);
    explicitMatches.forEach((match) => {
      const target = getFloatingPositionTarget(match);
      if (isFloatingControlCandidate(target, side)) {
        controls.push(target);
      }
    });

    if (controls.length === 0) {
      shadowRoot.querySelectorAll('button, [role="button"], a, div').forEach((candidate) => {
        const target = getFloatingPositionTarget(candidate);
        if (isFloatingControlCandidate(target, side)) {
          controls.push(target);
        }
      });
    }

    return dedupeElements(controls);
  }

  function collectSampledDocumentControls(side) {
    if (typeof document.elementsFromPoint !== 'function') {
      return [];
    }

    const viewportHeight = getViewportHeight();
    const sampleYs = [viewportHeight - 16, viewportHeight - 36, viewportHeight - 56, viewportHeight - 86];
    const sampleXs =
      side === 'right'
        ? [window.innerWidth - 16, window.innerWidth - 36, window.innerWidth - 56, window.innerWidth - 86]
        : [16, 36, 56, 86, 116, 146];

    const controls = [];
    sampleYs.forEach((y) => {
      sampleXs.forEach((x) => {
        document.elementsFromPoint(x, y).forEach((element) => {
          if (!(element instanceof HTMLElement)) {
            return;
          }

          const chain = [element, element.parentElement, element.parentElement?.parentElement].filter(Boolean);
          chain.forEach((candidate) => {
            const target = getFloatingPositionTarget(candidate);
            if (isFloatingControlCandidate(target, side)) {
              controls.push(target);
            }
          });
        });
      });
    });

    return dedupeElements(controls);
  }

  function getBottomOverlapFromRoot(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return 0;
    }

    let overlap = 0;
    CONSENT_BANNER_SELECTORS.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => {
        overlap = Math.max(overlap, getBottomOverlap(element));
      });
    });
    return overlap;
  }

  function getConsentBottomOverlap() {
    let overlap = getBottomOverlapFromRoot(document);

    const consentHost = document.querySelector(CONSENTMO_HOST_SELECTOR);
    if (consentHost instanceof HTMLElement) {
      overlap = Math.max(overlap, getBottomOverlap(consentHost));
      if (consentHost.shadowRoot) {
        overlap = Math.max(overlap, getBottomOverlapFromRoot(consentHost.shadowRoot));
      }
    }

    return overlap;
  }

  function getResolvedBottom() {
    const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    const baseGap = isMobile ? BASE_GAP_MOBILE : BASE_GAP_DESKTOP;
    const bottomOverlap = getConsentBottomOverlap();
    return Math.max(baseGap, Math.round(bottomOverlap + baseGap));
  }

  function getResolvedSideGap(leftControls) {
    const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    const fallbackGap = isMobile ? BASE_SIDE_GAP_MOBILE : BASE_SIDE_GAP_DESKTOP;
    if (!Array.isArray(leftControls) || leftControls.length === 0) {
      return fallbackGap;
    }

    const leftGaps = leftControls
      .map((control) => (control instanceof HTMLElement ? control.getBoundingClientRect().left : NaN))
      .filter((value) => Number.isFinite(value));

    if (leftGaps.length === 0) {
      return fallbackGap;
    }

    const mirroredGap = Math.round(Math.min(...leftGaps));
    return Math.max(0, Math.min(40, mirroredGap));
  }

  function applyControlBottom(control, resolvedBottom) {
    if (!(control instanceof HTMLElement)) {
      return;
    }

    control.style.setProperty('bottom', `${resolvedBottom}px`, 'important');
    control.style.setProperty('top', 'auto', 'important');
  }

  function getLeftFloatingControls() {
    const controls = [];

    const adaHost = document.querySelector(ADA_HOST_SELECTOR);
    if (adaHost instanceof HTMLElement && adaHost.shadowRoot) {
      controls.push(...collectShadowControls(adaHost.shadowRoot, ADA_CONTROL_SELECTORS, 'left'));
    }

    const consentHost = document.querySelector(CONSENTMO_HOST_SELECTOR);
    if (consentHost instanceof HTMLElement && consentHost.shadowRoot) {
      controls.push(...collectShadowControls(consentHost.shadowRoot, CONSENT_REOPEN_SELECTORS, 'left'));
    }

    controls.push(...collectSampledDocumentControls('left'));
    return dedupeElements(controls);
  }

  function applyLeftControlsFix(resolvedBottom) {
    const controls = getLeftFloatingControls();
    controls.forEach((control) => {
      control.style.setProperty('z-index', String(ADA_Z_INDEX), 'important');
      control.style.setProperty('visibility', 'visible', 'important');
      control.style.setProperty('opacity', '1', 'important');
      control.style.setProperty('pointer-events', 'auto', 'important');
      applyControlBottom(control, resolvedBottom);
    });

    return controls;
  }

  function getReferenceControlBottom(leftControls) {
    if (!Array.isArray(leftControls) || leftControls.length === 0) {
      return null;
    }

    const bottoms = leftControls
      .map((control) => (control instanceof HTMLElement ? control.getBoundingClientRect() : null))
      .filter((rect) => rect && Number.isFinite(rect.bottom) && rect.bottom > 0)
      .map((rect) => rect.bottom);

    if (bottoms.length === 0) {
      return null;
    }

    return Math.max(...bottoms);
  }

  function getAlignedTeaserBottom(defaultBottom, leftControls) {
    const referenceBottom = getReferenceControlBottom(leftControls);
    if (!Number.isFinite(referenceBottom)) {
      return defaultBottom;
    }

    const viewportHeight = getViewportHeight();
    const alignedBottom = Math.round(viewportHeight - referenceBottom);
    if (!Number.isFinite(alignedBottom)) {
      return defaultBottom;
    }

    return Math.max(0, alignedBottom);
  }

  function applyKlaviyoTeaserFix(resolvedBottom, resolvedSideGap, leftControls) {
    document.querySelectorAll(KLAVIYO_TEASER_SELECTOR).forEach((teaserElement) => {
      if (!(teaserElement instanceof HTMLElement)) {
        return;
      }

      const alignedBottom = getAlignedTeaserBottom(resolvedBottom, leftControls);
      teaserElement.style.setProperty('left', 'auto', 'important');
      teaserElement.style.setProperty('right', `${resolvedSideGap}px`, 'important');
      teaserElement.style.setProperty('top', 'auto', 'important');
      teaserElement.style.setProperty('bottom', `${alignedBottom}px`, 'important');
      teaserElement.style.setProperty('inset', `auto ${resolvedSideGap}px ${alignedBottom}px auto`, 'important');
      teaserElement.style.setProperty('margin', '0', 'important');
      teaserElement.style.setProperty('margin-top', '0', 'important');
      teaserElement.style.setProperty('margin-right', '0', 'important');
      teaserElement.style.setProperty('margin-bottom', '0', 'important');
      teaserElement.style.setProperty('margin-left', '0', 'important');
    });
  }

  function applyAllFloatingControlFixes() {
    const resolvedBottom = getResolvedBottom();
    const leftControls = isSafariBrowser() ? applyLeftControlsFix(resolvedBottom) : getLeftFloatingControls();
    const resolvedSideGap = getResolvedSideGap(leftControls);
    applyKlaviyoTeaserFix(resolvedBottom, resolvedSideGap, leftControls);
  }

  function queueFix() {
    if (isQueued) {
      return;
    }

    isQueued = true;
    window.requestAnimationFrame(() => {
      isQueued = false;
      applyAllFloatingControlFixes();
    });
  }

  function initialize() {
    if (!document.body) {
      return;
    }

    queueFix();

    window.addEventListener('resize', queueFix, { passive: true });
    window.addEventListener('orientationchange', queueFix, { passive: true });
    document.addEventListener('consentmoSignal_onLoad', queueFix);
    document.addEventListener('consentmoSignal', queueFix);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', queueFix, { passive: true });
      window.visualViewport.addEventListener('scroll', queueFix, { passive: true });
    }

    const observer = new MutationObserver(queueFix);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
